/* =====================================================================
 * WorkspaceListSidebar — 페이지 내부 워크스페이스 목록 패널
 * ===================================================================== */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader,
  Menu,
  MoreHorizontal,
  PanelLeft,
  Pencil,
  Pin,
  Plus,
  LogOut,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import type {
  Conversation,
  WorkspaceKind,
  WorkspaceMode,
  WorkspaceScope,
} from '@auw/workspace-utils';
import { WORKSPACE_MODES } from '@auw/workspace-utils';
import { useTranslation } from 'react-i18next';
import { SidebarItem } from '@auw/workspace-ui';
import { DropdownMenu } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { Tooltip } from '@auw/workspace-ui';
import {
  deleteSharedGroup,
  getContent,
  leaveSharedGroup,
  saveContent,
  updateItem,
  updateSharedGroupSettings,
} from '@auw/api-client';
import { emitAppEvent } from '@auw/stores';
import { buildConversationSettings } from '@auw/workspace-utils';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import {
  buildSidebarTree,
  extractWorkspaceHttpStatus,
  getPageDeleteLabel,
  getPageLabel,
  getSubPageLabel,
  groupConversationsByDate,
  hasChatMessages,
  type SidebarTreeShape,
} from '@auw/workspace-features';
import {
  AgentNewChatBtn,
  AgentQuickActionWrap,
  AgentSearchBtn,
  Container,
  CreateMenuBtn,
  CreateMenuRow,
  DropBadge,
  DropInsertLine,
  EmptyHint,
  FolderIcon,
  GroupTitle,
  Header,
  HeaderLeft,
  InlineIconBtn,
  List,
  LoadMoreTrigger,
  MainSidebarOpenBtn,
  PageIcon,
  RecentVirtuosoWrap,
  RecentWrap,
  SearchClearBtn,
  SearchInputWrap,
  SectionHeader,
  SectionRight,
  SectionTitle,
  SharedAvatar,
  SharedAvatarImg,
  SharedExtra,
  SharedLabel,
  SharedMetaRow,
  SharedStack,
  SidebarToggleBtn,
  TeamHint,
  TeamSelectBtn,
  TeamSharedActions,
  TeamSharedAvatar,
  TeamSharedAvatarImg,
  TeamSharedAvatars,
  TeamSharedExtra,
  TeamSharedItem,
  TeamSharedList,
  TeamSharedMainBtn,
  TeamSharedPinnedDivider,
  TeamSharedTitle,
  TeamWorkspaceWrap,
  ThumbnailGrid,
  Title,
  TreeActions,
  TreeItem,
  TreeNodeMain,
  TreeRenameInput,
  TreeSpacer,
  TreeTitle,
  TreeTitleWrap,
  TreeToggle,
  TreeWrap,
  WorkspaceActionBtn,
} from './WorkspaceListSidebar.styles';

interface Props {
  mode: WorkspaceMode;
  pageSidebarOpen: boolean;
  onTogglePageSidebar: () => void;
}

type RecentVirtualRow =
  | { type: 'group'; key: string; label: string }
  | { type: 'item'; key: string; id: string };
type DropRejectReason = 'target-not-folder' | 'cycle' | 'depth';
type DropBetweenState = {
  targetId: string;
  position: 'before' | 'after';
};

interface TeamSharedEntry {
  pageId: string;
  title: string;
  pinnedAt: string | null;
  updatedAt: number;
  members: Array<{
    user_id: string;
    name?: string | null;
    email?: string | null;
    picture?: string | null;
  }>;
}

const FOLDER_PAGE_MODES: WorkspaceMode[] = [
  'document',
];
const MAX_FOLDER_DEPTH = 2;
const DROP_EDGE_PX = 7;
const DROP_HYSTERESIS_PX = 4;
const RECENT_INITIAL_RENDER_COUNT = 40;

export const WorkspaceListSidebar: React.FC<Props> = ({
  mode,
  pageSidebarOpen,
  onTogglePageSidebar,
}) => {
  const { t } = useTranslation();
  const mainSidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setMainSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const conversations = useChatStore((s) => s.conversations);
  const conversationOrder = useChatStore((s) => s.conversationOrder);
  const workspaceOrder = useChatStore((s) => s.workspaceOrder);
  const activeId = useChatStore((s) => s.activeId);
  const deleteConversation = useChatStore((s) => s.deleteConversation);
  const removeConversationLocal = useChatStore((s) => s.removeConversationLocal);
  const addToast = useChatStore((s) => s.addToast);
  const renameConversation = useChatStore((s) => s.renameConversation);
  const moveConversationWorkspace = useChatStore((s) => s.moveConversationWorkspace);
  const loadMoreItems = useChatStore((s) => s.loadMoreItems);
  const modeTotalItemCount = useChatStore((s) => s.totalItemCountByMode[mode]);
  const loadingMoreItems = useChatStore((s) => s.loadingMoreItems);
  const {
    openWorkspaceDraft,
    openWorkspaceItem,
    createWorkspaceItem,
  } = useWorkspaceNavigate();

  const teams = useTeamStore((s) => s.teams);
  const teamsLoaded = useTeamStore((s) => s.isLoaded);
  const loadMyTeams = useTeamStore((s) => s.loadMyTeams);
  const currentUser = useUserStore((s) => s.user);
  const currentUserId = currentUser?.userId ?? '';

  const [sectionOpen, setSectionOpen] = useState({
    workspace: true,
    teamWorkspace: true,
    recent: true,
  });
  const [collapsedNodes, setCollapsedNodes] = useState<Record<string, boolean>>({});
  const [workspaceCreateOpen, setWorkspaceCreateOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropItemId, setDropItemId] = useState<string | null>(null);
  const [dropDeniedItemId, setDropDeniedItemId] = useState<string | null>(null);
  const [dropBetween, setDropBetween] = useState<DropBetweenState | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [teamMenu, setTeamMenu] = useState<{ open: boolean; x: number; y: number }>({
    open: false,
    x: 0,
    y: 0,
  });
  const [teamSharedAvatarErrorIds, setTeamSharedAvatarErrorIds] = useState<Record<string, boolean>>({});
  const [sharedMetaAvatarErrorIds, setSharedMetaAvatarErrorIds] = useState<Record<string, boolean>>({});
  const [nodeMenu, setNodeMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    direction: 'up' | 'down';
    convId: string | null;
  }>({ open: false, x: 0, y: 0, direction: 'down', convId: null });
  const [nodeCreateMenu, setNodeCreateMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    parentId: string | null;
    scope: WorkspaceScope;
    teamId: string | null;
  }>({
    open: false,
    x: 0,
    y: 0,
    parentId: null,
    scope: 'personal',
    teamId: null,
  });
  const [teamMenuWidth, setTeamMenuWidth] = useState<number>(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [listScrollParent, setListScrollParent] = useState<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const teamDropdownBtnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const listCallbackRef = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;
    setListScrollParent(node);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!teamsLoaded) {
      void loadMyTeams();
    }
  }, [loadMyTeams, mode, teamsLoaded]);

  useEffect(() => {
    if (teams.length === 0) {
      if (selectedTeamId !== '') setSelectedTeamId('');
      return;
    }
    const exists = teams.some((team) => team.team_id === selectedTeamId);
    if (!exists) {
      setSelectedTeamId(teams[0].team_id);
    }
  }, [teams, selectedTeamId]);

  const teamSharedEntries = useMemo<TeamSharedEntry[]>(() => {
    if (!selectedTeamId) return [];
    return Object.values(conversations)
      .filter(
        (conv) =>
          conv.mode === mode
          && conv.workspaceScope === 'team'
          && conv.workspaceTeamId === selectedTeamId
          && conv.workspaceKind === 'page'
          && !!conv.sharedGroup
          && conv.sharedGroup!.team_id === selectedTeamId
          && (!currentUserId || conv.sharedGroup!.member_ids.includes(currentUserId)),
      )
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map((conv) => ({
        pageId: conv.id,
        title: conv.sharedGroup!.title,
        pinnedAt: conv.pinnedAt ?? null,
        updatedAt: conv.updatedAt,
        members: conv.sharedGroup!.members,
      }));
  }, [selectedTeamId, conversations, mode, currentUserId]);

  const pinnedTeamSharedEntries = useMemo(
    () =>
      teamSharedEntries
        .filter((entry) => !!entry.pinnedAt)
        .sort((a, b) => {
          const aPinned = a.pinnedAt ? new Date(a.pinnedAt).getTime() : 0;
          const bPinned = b.pinnedAt ? new Date(b.pinnedAt).getTime() : 0;
          if (bPinned !== aPinned) return bPinned - aPinned;
          return b.updatedAt - a.updatedAt;
        }),
    [teamSharedEntries],
  );

  const unpinnedTeamSharedEntries = useMemo(
    () =>
      teamSharedEntries
        .filter((entry) => !entry.pinnedAt)
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [teamSharedEntries],
  );

  useEffect(() => {
    if (editingId) {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }
  }, [editingId]);

  const markTeamSharedAvatarError = (avatarKey: string) => {
    setTeamSharedAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  const markSharedMetaAvatarError = (avatarKey: string) => {
    setSharedMetaAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  const rankMap = useMemo(
    () => new Map(workspaceOrder.map((id, idx) => [id, idx])),
    [workspaceOrder],
  );

  const modeConversationMap = useMemo(() => {
    const map: Record<string, Conversation> = {};
    for (const conv of Object.values(conversations)) {
      if (conv.mode === mode) map[conv.id] = conv;
    }
    return map;
  }, [conversations, mode]);
  const isAgentMode = mode === 'agent';
  const isLiveAgentMode = mode === 'live-agent';
  const isFolderPageMode = FOLDER_PAGE_MODES.includes(mode);
  const hasTeams = teams.length > 0;
  const modeInfoForLabel = WORKSPACE_MODES[mode];
  const modeLabel = modeInfoForLabel ? t(modeInfoForLabel.labelKey) : mode;

  const personalConversations = useMemo(
    () =>
      Object.values(modeConversationMap).filter(
        (conv) => conv.workspaceScope === 'personal',
      ),
    [modeConversationMap],
  );

  const personalTree = useMemo(
    () => buildSidebarTree(personalConversations, rankMap),
    [personalConversations, rankMap],
  );

  const recentConversationMap = useMemo(() => {
    const map: Record<string, Conversation> = {};
    for (const conv of Object.values(modeConversationMap)) {
      if (conv.workspaceKind !== 'folder') {
        // 팀 공유 아이템은 팀 워크스페이스 섹션에서만 표시한다.
        if (conv.workspaceScope === 'team' && conv.sharedGroup) continue;
        // agent 모드에서는 과거 정책으로 남은 빈 personal page를 최근 목록에서 숨김
        if (
          isAgentMode
          && conv.workspaceScope === 'personal'
          && conv.workspaceKind === 'page'
          && !conv.sharedGroup
          && conv.historyLoaded === true
          && !hasChatMessages(conv)
        ) {
          continue;
        }
        map[conv.id] = conv;
      }
    }
    return map;
  }, [isAgentMode, modeConversationMap]);

  const recentOrder = useMemo(
    () =>
      conversationOrder
        .filter((id) => !!recentConversationMap[id])
        .sort((a, b) => {
          const aConv = recentConversationMap[a];
          const bConv = recentConversationMap[b];
          const aPinned = aConv?.pinnedAt ? new Date(aConv.pinnedAt).getTime() : 0;
          const bPinned = bConv?.pinnedAt ? new Date(bConv.pinnedAt).getTime() : 0;
          if (bPinned !== aPinned) return bPinned - aPinned;
          return (bConv?.updatedAt ?? 0) - (aConv?.updatedAt ?? 0);
        }),
    [conversationOrder, recentConversationMap],
  );
  const recentGroups = useMemo(
    () => groupConversationsByDate(recentConversationMap, recentOrder),
    [recentConversationMap, recentOrder],
  );

  const filteredRecentGroups = useMemo(() => {
    if (!searchQuery.trim()) return recentGroups;
    const q = searchQuery.trim().toLowerCase();
    return recentGroups
      .map((group) => ({
        ...group,
        ids: group.ids.filter((id) =>
          recentConversationMap[id]?.title?.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.ids.length > 0);
  }, [recentGroups, searchQuery, recentConversationMap]);
  const recentVirtualRows = useMemo<RecentVirtualRow[]>(() => {
    const rows: RecentVirtualRow[] = [];
    const hideGroupTitle = searchQuery.trim().length > 0;
    filteredRecentGroups.forEach((group, groupIndex) => {
      if (!hideGroupTitle) {
        rows.push({
          type: 'group',
          key: `recent-group:${group.label}:${groupIndex}`,
          label: group.label,
        });
      }
      group.ids.forEach((id) => {
        rows.push({
          type: 'item',
          key: `recent-item:${id}`,
          id,
        });
      });
    });
    return rows;
  }, [filteredRecentGroups, searchQuery]);

  const childrenByParentMap = useMemo(() => {
    const byParent: Record<string, string[]> = {};
    for (const conv of Object.values(modeConversationMap)) {
      const key = conv.workspaceParentId ?? 'root';
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(conv.id);
    }
    return byParent;
  }, [modeConversationMap]);

  const activeModeConversation = activeId ? modeConversationMap[activeId] : null;

  /* ── 무한 스크롤: 최근 항목 리스트 하단 도달 시 다음 페이지 로드 ── */
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadedModeWorkspaceCount = useMemo(
    () => workspaceOrder.filter((id) => !!modeConversationMap[id]).length,
    [modeConversationMap, workspaceOrder],
  );
  const hasMoreItems = typeof modeTotalItemCount === 'number' && loadedModeWorkspaceCount < modeTotalItemCount;

  const loadMoreCallback = useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreRef.current = node;
    },
    [],
  );

  useEffect(() => {
    if (isLiveAgentMode) return;
    if (typeof modeTotalItemCount === 'number') return;
    if (loadingMoreItems) return;
    void loadMoreItems({ mode });
  }, [loadMoreItems, loadingMoreItems, mode, modeTotalItemCount, isLiveAgentMode]);

  useEffect(() => {
    if (isLiveAgentMode) return;
    const el = loadMoreRef.current;
    if (!el || !hasMoreItems) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingMoreItems) {
          void loadMoreItems({ mode });
        }
      },
      { root: listRef.current, rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreItems, loadingMoreItems, loadMoreItems, mode, isLiveAgentMode]);
  const getFolderDepth = (nodeId: string | null): number => {
    let depth = 0;
    let currentId = nodeId;
    const visited = new Set<string>();
    while (currentId) {
      if (visited.has(currentId)) break;
      visited.add(currentId);
      const current = modeConversationMap[currentId];
      if (!current) break;
      if (current.workspaceKind === 'folder') depth += 1;
      currentId = current.workspaceParentId;
    }
    return depth;
  };

  const getSubtreeFolderHeight = (nodeId: string, visited: Set<string> = new Set()): number => {
    if (visited.has(nodeId)) return 0;
    visited.add(nodeId);
    const current = modeConversationMap[nodeId];
    if (!current) return 0;
    let childMax = 0;
    for (const childId of childrenByParentMap[nodeId] ?? []) {
      childMax = Math.max(childMax, getSubtreeFolderHeight(childId, visited));
    }
    visited.delete(nodeId);
    return (current.workspaceKind === 'folder' ? 1 : 0) + childMax;
  };

  const showFolderDepthToast = () => {
    addToast({
      type: 'warning',
      title: t('workspace.folderDepthLimit'),
      description: t('workspace.folderDepthLimitDesc', { max: MAX_FOLDER_DEPTH }),
    });
  };

  const showDropRejectToast = (reason: DropRejectReason) => {
    if (reason === 'depth') {
      showFolderDepthToast();
      return;
    }
    if (reason === 'target-not-folder') {
      addToast({
        type: 'warning',
        title: t('workspace.cannotMove'),
        description: t('workspace.cannotMoveFolderOnly'),
      });
      return;
    }
    addToast({
      type: 'warning',
      title: t('workspace.cannotMove'),
      description: t('workspace.cannotMoveCycle'),
    });
  };

  const isAncestorConversation = (ancestorId: string, nodeId: string): boolean => {
    let current = modeConversationMap[nodeId];
    const visited = new Set<string>();
    while (current?.workspaceParentId) {
      const parentId = current.workspaceParentId;
      if (parentId === ancestorId) return true;
      if (visited.has(parentId)) break;
      visited.add(parentId);
      current = modeConversationMap[parentId];
    }
    return false;
  };

  const canMoveToParent = (
    dragId: string,
    parentId: string | null,
  ): { valid: true } | { valid: false; reason: DropRejectReason } => {
    if (parentId === dragId) {
      return { valid: false, reason: 'cycle' };
    }
    if (parentId && isAncestorConversation(dragId, parentId)) {
      return { valid: false, reason: 'cycle' };
    }
    if (isFolderPageMode) {
      const parentDepth = getFolderDepth(parentId);
      const subtreeHeight = getSubtreeFolderHeight(dragId);
      const nextDepth = parentDepth + subtreeHeight;
      if (nextDepth > MAX_FOLDER_DEPTH) {
        return { valid: false, reason: 'depth' };
      }
    }
    return { valid: true };
  };

  const canDropToItem = (
    dragId: string,
    target: Conversation,
  ): { valid: true } | { valid: false; reason: DropRejectReason } => {
    if (target.workspaceKind !== 'folder') {
      return { valid: false, reason: 'target-not-folder' };
    }
    return canMoveToParent(dragId, target.id);
  };

  const setDropBetweenStable = (next: DropBetweenState | null) => {
    setDropBetween((prev) => {
      const same =
        prev?.targetId === next?.targetId &&
        prev?.position === next?.position;
      return same ? prev : next;
    });
  };

  const toggleSection = (key: keyof typeof sectionOpen) => {
    setSectionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSectionHeaderKeyToggle = (
    event: React.KeyboardEvent<HTMLDivElement>,
    key: keyof typeof sectionOpen,
  ) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleSection(key);
    }
  };

  const toggleNode = (id: string) => {
    setCollapsedNodes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCreateWorkspace = (params: {
    scope: WorkspaceScope;
    kind?: WorkspaceKind;
    teamId?: string | null;
    parentId?: string | null;
  }) => {
    // 새 폴더 생성 시 depth 제한 (루트 폴더 depth=1)
    if (params.kind === 'folder' && params.parentId) {
      const nextFolderDepth = getFolderDepth(params.parentId) + 1;
      if (nextFolderDepth > MAX_FOLDER_DEPTH) {
        showFolderDepthToast();
        return;
      }
    }

    const isPage = (params.kind ?? 'page') === 'page';
    createWorkspaceItem({
      mode,
      scope: params.scope,
      kind: params.kind ?? 'page',
      teamId: params.teamId ?? null,
      parentId: params.parentId ?? null,
      startPagePending: !isAgentMode && isPage,
    });
    setWorkspaceCreateOpen(false);
    setNodeCreateMenu((prev) => ({ ...prev, open: false }));
  };

  const handleCreateAgentChat = () => {
    if (isLiveAgentMode) {
      import('@auw/stores').then(({ useLiveAgentStore }) => {
        useLiveAgentStore.getState().clearSession();
      });
      openWorkspaceDraft('live-agent');
    } else {
      openWorkspaceDraft('agent');
    }
  };

  const setConversationLocal = (conv: Conversation) => {
    useChatStore.setState((s) => ({
      conversations: {
        ...s.conversations,
        [conv.id]: conv,
      },
    }));
  };

  const handlePinConversation = async (conv: Conversation) => {
    const nowIso = new Date().toISOString();
    const nextConv: Conversation = {
      ...conv,
      pinnedAt: nowIso,
    };
    setConversationLocal(nextConv);
    try {
      if (conv.sharedGroup) {
        await updateSharedGroupSettings(conv.id, { pinned: true });
      } else {
        await updateItem(conv.id, { settings: buildConversationSettings(nextConv) });
      }
      addToast({
        type: 'success',
        title: t('toast.pinSuccess'),
        description: t('toast.pinSuccessDesc'),
      });
    } catch (err) {
      setConversationLocal(conv);
      const status = extractWorkspaceHttpStatus(err);
      addToast({
        type: status === 409 ? 'warning' : 'error',
        title: t('toast.pinFail'),
        description: status === 409 ? t('error.teamSpaceDeleted') : t('toast.retryLater'),
      });
    }
  };

  const handleUnpinConversation = async (conv: Conversation) => {
    const nextConv: Conversation = {
      ...conv,
      pinnedAt: null,
    };
    setConversationLocal(nextConv);
    try {
      if (conv.sharedGroup) {
        await updateSharedGroupSettings(conv.id, { pinned: false });
      } else {
        await updateItem(conv.id, { settings: buildConversationSettings(nextConv) });
      }
      addToast({
        type: 'success',
        title: t('toast.unpinSuccess'),
        description: t('toast.unpinSuccessDesc'),
      });
    } catch (err) {
      setConversationLocal(conv);
      const status = extractWorkspaceHttpStatus(err);
      addToast({
        type: status === 409 ? 'warning' : 'error',
        title: t('toast.unpinFail'),
        description: status === 409 ? t('error.teamSpaceDeleted') : t('toast.retryLater'),
      });
    }
  };

  const handleRenameSharedConversation = async (conv: Conversation, nextTitle: string) => {
    const prevTitle = conv.title;
    const prevSharedTitle = conv.sharedGroup?.title ?? prevTitle;
    const nextConv: Conversation = {
      ...conv,
      title: nextTitle,
      sharedGroup: conv.sharedGroup
        ? { ...conv.sharedGroup, title: nextTitle }
        : conv.sharedGroup,
      updatedAt: Date.now(),
    };
    setConversationLocal(nextConv);
    try {
      await updateSharedGroupSettings(conv.id, { title: nextTitle });
    } catch (err) {
      const status = extractWorkspaceHttpStatus(err);
      setConversationLocal({
        ...nextConv,
        title: prevTitle,
        sharedGroup: nextConv.sharedGroup
          ? { ...nextConv.sharedGroup, title: prevSharedTitle }
          : nextConv.sharedGroup,
      });
      addToast({
        type: status === 409 ? 'warning' : 'error',
        title: t('toast.renameFail'),
        description: status === 409 ? t('error.teamSpaceDeleted') : t('toast.retryLater'),
      });
    }
  };

  const handlePromptRenameSharedConversation = (conv: Conversation) => {
    const currentTitle = conv.sharedGroup?.title ?? conv.title;
    const input = window.prompt(t('workspace.enterChatName'), currentTitle);
    if (input === null) return;
    const trimmed = input.trim();
    if (!trimmed || trimmed === currentTitle) return;
    void handleRenameSharedConversation(conv, trimmed);
  };

  const handleLeaveSharedConversation = async (conv: Conversation) => {
    try {
      await leaveSharedGroup(conv.id);
      removeConversationLocal(conv.id);
      addToast({
        type: 'success',
        title: t('workspace.leftChat'),
        description: t('workspace.leftChatDesc'),
      });
    } catch (err) {
      const status = extractWorkspaceHttpStatus(err);
      addToast({
        type: status === 409 ? 'warning' : 'error',
        title: t('workspace.leaveFail'),
        description: status === 409 ? t('error.teamSpaceDeleted') : t('toast.retryLater'),
      });
    }
  };

  const handleDeleteSharedConversation = async (conv: Conversation) => {
    try {
      await deleteSharedGroup(conv.id);
      removeConversationLocal(conv.id);
      addToast({
        type: 'success',
        title: t('workspace.groupChatDeleted'),
      });
    } catch (err) {
      const status = extractWorkspaceHttpStatus(err);
      if (status === 403) {
        addToast({
          type: 'warning',
          title: t('workspace.noDeletePermission'),
          description: t('workspace.noDeletePermissionDesc'),
        });
        return;
      }
      addToast({
        type: status === 409 ? 'warning' : 'error',
        title: t('workspace.groupChatDeleteFail'),
        description: status === 409 ? t('error.teamSpaceDeleted') : t('toast.retryLater'),
      });
    }
  };

  const openSharedGroupConversation = async (entry: TeamSharedEntry) => {
    try {
      openWorkspaceItem(entry.pageId);
    } catch {
      addToast({
        type: 'error',
        title: t('workspace.groupChatOpenFail'),
        description: t('workspace.groupChatOpenFailDesc'),
      });
    }
  };

  const renderTeamSharedEntry = (entry: TeamSharedEntry): React.ReactNode => {
    const conv = modeConversationMap[entry.pageId];
    if (!conv) return null;

    const visible = entry.members.slice(0, 3);
    const extra = Math.max(0, entry.members.length - visible.length);

    return (
      <TeamSharedItem key={entry.pageId}>
        <TeamSharedMainBtn
          type="button"
          onClick={() => {
            void openSharedGroupConversation(entry);
          }}
        >
          <TeamSharedAvatars>
            {visible.map((member, idx) => {
              const memberPicture = member.user_id === currentUser?.userId
                ? (currentUser?.picture ?? null)
                : (member.picture ?? null);
              const avatarKey = `${entry.pageId}:${member.user_id}:${idx}:${memberPicture || 'none'}`;
              const fallback = (member.name || member.email || member.user_id).charAt(0).toUpperCase();
              return (
                <TeamSharedAvatar key={`${entry.pageId}-${member.user_id}-${idx}`} $idx={idx}>
                  {memberPicture && !teamSharedAvatarErrorIds[avatarKey] ? (
                    <TeamSharedAvatarImg
                      src={memberPicture}
                      alt={member.name || member.user_id}
                      onError={() => markTeamSharedAvatarError(avatarKey)}
                    />
                  ) : (
                    fallback
                  )}
                </TeamSharedAvatar>
              );
            })}
            {extra > 0 && <TeamSharedExtra $idx={visible.length}>+{extra}</TeamSharedExtra>}
          </TeamSharedAvatars>
          <TeamSharedTitle>{entry.title}</TeamSharedTitle>
        </TeamSharedMainBtn>
        <TeamSharedActions>
          <WorkspaceActionBtn
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              const viewportH = window.innerHeight;
              const menuApproxH = 168;
              const y = rect.bottom + menuApproxH > viewportH ? rect.top - 4 : rect.bottom;
              const direction: 'up' | 'down' =
                rect.bottom + menuApproxH > viewportH ? 'up' : 'down';
              setNodeMenu({
                open: true,
                x: rect.right,
                y,
                direction,
                convId: conv.id,
              });
            }}
          >
            <MoreHorizontal size={14} />
          </WorkspaceActionBtn>
        </TeamSharedActions>
      </TeamSharedItem>
    );
  };

  const handleDropToItem = (target: Conversation) => {
    if (!draggingId || draggingId === target.id) return;
    const dropCheck = canDropToItem(draggingId, target);
    if (!dropCheck.valid) {
      showDropRejectToast(dropCheck.reason);
      return;
    }

    moveConversationWorkspace(draggingId, {
      parentId: target.id,
      scope: target.workspaceScope,
      teamId: target.workspaceTeamId,
    });
    setCollapsedNodes((prev) => ({ ...prev, [target.id]: false }));
    setDropBetweenStable(null);
  };

  const handleDropBetween = (target: Conversation, position: 'before' | 'after') => {
    if (!draggingId || draggingId === target.id) return;
    const nextParentId = target.workspaceParentId ?? null;
    const dropCheck = canMoveToParent(draggingId, nextParentId);
    if (!dropCheck.valid) {
      showDropRejectToast(dropCheck.reason);
      return;
    }

    moveConversationWorkspace(draggingId, {
      parentId: nextParentId,
      scope: target.workspaceScope,
      teamId: target.workspaceTeamId,
      insertBeforeId: position === 'before' ? target.id : undefined,
      insertAfterId: position === 'after' ? target.id : undefined,
    });
    if (nextParentId) {
      setCollapsedNodes((prev) => ({ ...prev, [nextParentId]: false }));
    }
  };

  const getDropPlacement = (
    event: React.DragEvent<HTMLDivElement>,
    nodeId: string,
  ): 'before' | 'inside' | 'after' => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetY = event.clientY - rect.top;
    const edge = Math.min(DROP_EDGE_PX, Math.max(4, rect.height / 3));
    const activeLineForNode = dropBetween?.targetId === nodeId ? dropBetween.position : null;
    const topBoundary = activeLineForNode === 'before' ? edge + DROP_HYSTERESIS_PX : edge;
    const bottomBoundary =
      activeLineForNode === 'after'
        ? rect.height - (edge + DROP_HYSTERESIS_PX)
        : rect.height - edge;

    if (offsetY <= topBoundary) return 'before';
    if (offsetY >= bottomBoundary) return 'after';
    return 'inside';
  };

  const renderTree = (
    nodeId: string,
    tree: SidebarTreeShape,
    depth: number,
  ): React.ReactNode => {
    const conv = modeConversationMap[nodeId];
    if (!conv) return null;
    const childIds = tree.childrenByParent[nodeId] ?? [];
    const hasChildren = childIds.length > 0;
    const hasNestedContent = hasChildren;
    const collapsed = collapsedNodes[nodeId] ?? false;
    const isFolder = conv.workspaceKind === 'folder';
    const isTreeItemActive = !isFolder && nodeId === activeId;
    const isEditing = editingId === nodeId;
    const isDropActive = dropItemId === nodeId;
    const isDropDenied = dropDeniedItemId === nodeId;
    const isDropLineBefore = dropBetween?.targetId === nodeId && dropBetween.position === 'before';
    const isDropLineAfter = dropBetween?.targetId === nodeId && dropBetween.position === 'after';
    const showDropHint = isDropActive || isDropDenied;
    const sharedMembers = conv.sharedGroup?.members ?? [];
    const visibleSharedMembers = sharedMembers.slice(0, 3);
    const extraSharedCount = Math.max(0, sharedMembers.length - visibleSharedMembers.length);

    const commitRename = () => {
      const trimmed = editTitle.trim();
      if (trimmed && trimmed !== conv.title) {
        if (conv.sharedGroup) {
          void handleRenameSharedConversation(conv, trimmed);
        } else {
          renameConversation(conv.id, trimmed);
        }
      }
      setEditingId(null);
    };

    const cancelRename = () => {
      setEditTitle(conv.title);
      setEditingId(null);
    };

    return (
      <React.Fragment key={nodeId}>
        {isDropLineBefore && <DropInsertLine $depth={depth} />}
        <TreeItem
          data-tree-item="true"
          data-node-id={nodeId}
          $active={isTreeItemActive}
          $depth={depth}
          $dropActive={isDropActive}
          $dropDenied={isDropDenied}
          $dragging={draggingId === nodeId}
          role="button"
          tabIndex={isEditing ? -1 : 0}
          aria-expanded={isFolder ? !collapsed : undefined}
          draggable={!isEditing}
          onClick={() => {
            if (isEditing) return;
            if (isFolder) {
              toggleNode(nodeId);
            } else {
              openWorkspaceItem(nodeId);
            }
          }}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            if (isEditing) return;
            if (isFolder) {
              toggleNode(nodeId);
            } else {
              openWorkspaceItem(nodeId);
            }
          }}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', nodeId);
            setDraggingId(nodeId);
            setDropItemId(null);
            setDropDeniedItemId(null);
            setDropBetweenStable(null);
          }}
          onDragEnd={() => {
            setDraggingId(null);
            setDropItemId(null);
            setDropDeniedItemId(null);
            setDropBetweenStable(null);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (draggingId && draggingId !== nodeId) {
              const placement = getDropPlacement(e, nodeId);
              if (placement === 'before' || placement === 'after') {
                const betweenCheck = canMoveToParent(draggingId, conv.workspaceParentId ?? null);
                if (betweenCheck.valid) {
                  setDropBetweenStable({ targetId: nodeId, position: placement });
                  setDropItemId(null);
                  setDropDeniedItemId(null);
                  e.dataTransfer.dropEffect = 'move';
                } else {
                  setDropBetweenStable(null);
                  setDropItemId(null);
                  setDropDeniedItemId(null);
                  e.dataTransfer.dropEffect = 'none';
                }
                return;
              }

              setDropBetweenStable(null);
              const dropCheck = canDropToItem(draggingId, conv);
              setDropItemId(dropCheck.valid ? nodeId : null);
              setDropDeniedItemId(dropCheck.valid ? null : nodeId);
              e.dataTransfer.dropEffect = dropCheck.valid ? 'move' : 'none';
            }
          }}
          onDragLeave={(e) => {
            e.stopPropagation();
            const next = e.relatedTarget as Node | null;
            if (next && e.currentTarget.contains(next)) return;
            if (dropItemId === nodeId) setDropItemId(null);
            if (dropDeniedItemId === nodeId) setDropDeniedItemId(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const placement = getDropPlacement(e, nodeId);
            if (placement === 'before' || placement === 'after') {
              handleDropBetween(conv, placement);
            } else {
              handleDropToItem(conv);
            }
            setDropItemId(null);
            setDropDeniedItemId(null);
            setDropBetweenStable(null);
            setDraggingId(null);
          }}
        >
          <TreeNodeMain>
            <TreeToggle
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (hasNestedContent) toggleNode(nodeId);
              }}
            >
              {hasNestedContent ? (
                collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />
              ) : (
                <TreeSpacer />
              )}
            </TreeToggle>
            {isFolder ? <FolderIcon size={13} /> : <PageIcon size={13} />}
            {isEditing ? (
              <TreeRenameInput
                ref={editInputRef}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') cancelRename();
                }}
                onBlur={commitRename}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <TreeTitleWrap>
                <TreeTitle>{conv.title}</TreeTitle>
                {conv.workspaceKind === 'page' && conv.sharedGroup && (
                  <SharedMetaRow title={conv.sharedGroup.title}>
                    <SharedStack>
                      {visibleSharedMembers.map((member, idx) => {
                        const memberPicture = member.user_id === currentUser?.userId
                          ? (currentUser?.picture ?? null)
                          : (member.picture ?? null);
                        const avatarKey = `tree:${conv.id}:${member.user_id}:${idx}:${memberPicture || 'none'}`;
                        const fallback = (member.name || member.email || member.user_id).charAt(0).toUpperCase();
                        return (
                          <SharedAvatar key={`${member.user_id}-${idx}`} $idx={idx}>
                            {memberPicture && !sharedMetaAvatarErrorIds[avatarKey] ? (
                              <SharedAvatarImg
                                src={memberPicture}
                                alt={member.name || member.user_id}
                                onError={() => markSharedMetaAvatarError(avatarKey)}
                              />
                            ) : (
                              fallback
                            )}
                          </SharedAvatar>
                        );
                      })}
                      {extraSharedCount > 0 && (
                        <SharedExtra $idx={visibleSharedMembers.length}>
                          +{extraSharedCount}
                        </SharedExtra>
                      )}
                    </SharedStack>
                    <SharedLabel>{conv.sharedGroup.title}</SharedLabel>
                  </SharedMetaRow>
                )}
              </TreeTitleWrap>
            )}
          </TreeNodeMain>
          <TreeActions
            $visible={isEditing}
            $showDropState={showDropHint}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {isEditing ? (
              <>
                <WorkspaceActionBtn
                  type="button"
                  title={t('workspace.cancelEdit')}
                  onClick={cancelRename}
                >
                  <X size={14} />
                </WorkspaceActionBtn>
                <WorkspaceActionBtn
                  type="button"
                  title={t('workspace.confirmEdit')}
                  onClick={commitRename}
                >
                  <Check size={14} />
                </WorkspaceActionBtn>
              </>
            ) : (
              <>
                {showDropHint && (
                  <DropBadge $valid={isDropActive}>
                    {isDropActive ? <Check size={11} /> : <X size={11} />}
                    <span>{isDropActive ? t('workspace.canDrop') : t('workspace.folderOnly')}</span>
                  </DropBadge>
                )}
                {isFolderPageMode && isFolder && (
                  <WorkspaceActionBtn
                    type="button"
                    title={t('workspace.createSubItem')}
                    onClick={(e) => {
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      setNodeCreateMenu({
                        open: true,
                        x: rect.left,
                        y: rect.bottom + 4,
                        parentId: conv.id,
                        scope: conv.workspaceScope,
                        teamId: conv.workspaceTeamId ?? null,
                      });
                    }}
                  >
                    <Plus size={14} />
                  </WorkspaceActionBtn>
                )}
                <WorkspaceActionBtn
                  type="button"
                  title={t('tooltip.moreActions')}
                  onClick={(e) => {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const direction: 'up' | 'down' =
                      rect.bottom > window.innerHeight * 0.6 ? 'up' : 'down';
                    const y = direction === 'up' ? rect.top : rect.bottom + 4;
                    setNodeMenu({
                      open: true,
                      x: rect.right,
                      y,
                      direction,
                      convId: conv.id,
                    });
                  }}
                >
                  <MoreHorizontal size={14} />
                </WorkspaceActionBtn>
              </>
            )}
          </TreeActions>
        </TreeItem>
        {isDropLineAfter && <DropInsertLine $depth={depth} />}
        {hasChildren && !collapsed && childIds.map((childId) => renderTree(childId, tree, depth + 1))}
      </React.Fragment>
    );
  };

  /* ---- nodeMenu 항목 계산 ---- */
  const nodeMenuConv = nodeMenu.convId ? modeConversationMap[nodeMenu.convId] : null;
  const isGroupNode = !!nodeMenuConv?.sharedGroup;
  const nodeMenuItems = nodeMenuConv
    ? [
        {
          id: 'rename',
          label: t('tooltip.rename'),
          icon: <Pencil size={14} />,
          onClick: () => {
            if (isGroupNode) {
              handlePromptRenameSharedConversation(nodeMenuConv);
              return;
            }
            setEditingId(nodeMenuConv.id);
            setEditTitle(nodeMenuConv.title);
          },
        },
        {
          id: 'delete',
          label: isGroupNode
            ? t('workspace.deleteGroupChat')
            : (nodeMenuConv.workspaceKind === 'folder' ? t('workspace.deleteFolder') : getPageDeleteLabel(mode)),
          icon: <Trash2 size={14} />,
          danger: true,
          onClick: () => {
            if (isGroupNode) {
              void handleDeleteSharedConversation(nodeMenuConv);
            } else {
              deleteConversation(nodeMenuConv.id);
            }
          },
        },
        ...(nodeMenuConv.workspaceKind === 'page' && nodeMenuConv.pinnedAt
          ? [
              {
                id: 'unpin',
                label: t('tooltip.unpin'),
                icon: <Pin size={14} />,
                onClick: () => void handleUnpinConversation(nodeMenuConv),
              },
            ]
          : nodeMenuConv.workspaceKind === 'page'
            ? [
                {
                  id: 'pin',
                  label: t('tooltip.pin'),
                  icon: <Pin size={14} />,
                  onClick: () => void handlePinConversation(nodeMenuConv),
                },
              ]
            : []),
        ...(isGroupNode
          ? [
              {
                id: 'leave',
                label: t('workspace.leaveChat'),
                icon: <LogOut size={14} />,
                onClick: () => void handleLeaveSharedConversation(nodeMenuConv),
              },
            ]
          : []),
      ]
    : [];
  return (
    <Container>
      <Header>
        <HeaderLeft>
          {!mainSidebarOpen && (
            <MainSidebarOpenBtn
              type="button"
              onClick={() => setMainSidebarOpen(true)}
              title={t('workspace.expandMainPanel')}
            >
              <PanelLeft size={16} />
            </MainSidebarOpenBtn>
          )}
          <Title>{modeLabel}</Title>
        </HeaderLeft>
        <SidebarToggleBtn
          type="button"
          onClick={onTogglePageSidebar}
          title={pageSidebarOpen ? t('workspace.collapsePageSidebar') : t('workspace.expandPageSidebar')}
        >
          <Menu size={16} />
        </SidebarToggleBtn>
      </Header>

      <List ref={listCallbackRef}>
        {!isAgentMode && !isLiveAgentMode && (
          <>
            <SectionHeader
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('workspace')}
              onKeyDown={(e) => handleSectionHeaderKeyToggle(e, 'workspace')}
            >
              <SectionTitle>{t('sidebar.workspace')}</SectionTitle>
              <SectionRight>
                <InlineIconBtn
                  type="button"
                  title={t('workspace.newWorkspace')}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFolderPageMode) {
                      setWorkspaceCreateOpen((prev) => !prev);
                    } else {
                      handleCreateWorkspace({ scope: 'personal', kind: 'page' });
                    }
                  }}
                >
                  <Plus size={13} />
                </InlineIconBtn>
                {sectionOpen.workspace ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </SectionRight>
            </SectionHeader>
            {sectionOpen.workspace && (
              <>
                {isFolderPageMode && workspaceCreateOpen && (
                  <CreateMenuRow>
                    <CreateMenuBtn
                      type="button"
                      onClick={() => handleCreateWorkspace({ scope: 'personal', kind: 'folder' })}
                    >
                      {t('chat.newFolder')}
                    </CreateMenuBtn>
                    <CreateMenuBtn
                      type="button"
                      onClick={() => handleCreateWorkspace({ scope: 'personal', kind: 'page' })}
                    >
                      {getPageLabel(mode)}
                    </CreateMenuBtn>
                  </CreateMenuRow>
                )}
                <TreeWrap
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!draggingId) return;
                    const targetEl = e.target as HTMLElement | null;
                    if (targetEl?.closest('[data-tree-item="true"]')) return;
                    const wrap = e.currentTarget as HTMLElement;
                    const itemEls = Array.from(
                      wrap.querySelectorAll<HTMLElement>('[data-tree-item="true"][data-node-id]'),
                    );
                    if (itemEls.length === 0) return;

                    let anchorEl: HTMLElement = itemEls[itemEls.length - 1];
                    let position: 'before' | 'after' = 'after';
                    for (const itemEl of itemEls) {
                      const rect = itemEl.getBoundingClientRect();
                      if (e.clientY <= rect.top + rect.height / 2) {
                        anchorEl = itemEl;
                        position = 'before';
                        break;
                      }
                    }

                    const anchorId = anchorEl.dataset.nodeId;
                    if (!anchorId || anchorId === draggingId) return;
                    const anchorConv = modeConversationMap[anchorId];
                    if (!anchorConv) return;
                    const betweenCheck = canMoveToParent(draggingId, anchorConv.workspaceParentId ?? null);
                    if (!betweenCheck.valid) {
                      setDropBetweenStable(null);
                      setDropItemId(null);
                      setDropDeniedItemId(null);
                      e.dataTransfer.dropEffect = 'none';
                      return;
                    }
                    setDropBetweenStable({ targetId: anchorId, position });
                    setDropItemId(null);
                    setDropDeniedItemId(null);
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDragLeave={(e) => {
                    const next = e.relatedTarget as Node | null;
                    if (next && e.currentTarget.contains(next)) return;
                    setDropBetweenStable(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const targetEl = e.target as HTMLElement | null;
                    if (targetEl?.closest('[data-tree-item="true"]')) return;
                    if (dropBetween) {
                      const targetConv = modeConversationMap[dropBetween.targetId];
                      if (targetConv) {
                        handleDropBetween(targetConv, dropBetween.position);
                      }
                    }
                    setDropBetweenStable(null);
                    setDropItemId(null);
                    setDropDeniedItemId(null);
                    setDraggingId(null);
                  }}
                >
                  {personalTree.roots.length === 0 ? (
                    isFolderPageMode ? (
                      workspaceCreateOpen ? null : (
                        <CreateMenuRow>
                          <CreateMenuBtn
                            type="button"
                            onClick={() => handleCreateWorkspace({ scope: 'personal', kind: 'folder' })}
                          >
                            {t('chat.newFolder')}
                          </CreateMenuBtn>
                          <CreateMenuBtn
                            type="button"
                            onClick={() => handleCreateWorkspace({ scope: 'personal', kind: 'page' })}
                          >
                            {getPageLabel(mode)}
                          </CreateMenuBtn>
                        </CreateMenuRow>
                      )
                    ) : (
                      <EmptyHint>{t('workspace.noWorkspace')}</EmptyHint>
                    )
                  ) : (
                    personalTree.roots.map((id) => renderTree(id, personalTree, 0))
                  )}
                </TreeWrap>
              </>
            )}
          </>
        )}

        {(isAgentMode || isLiveAgentMode) && (
          <AgentQuickActionWrap>
            <AgentNewChatBtn
              type="button"
              onClick={handleCreateAgentChat}
            >
              <Plus size={14} />
              {isLiveAgentMode ? t('liveAgent.newSession') : t('chat.newConversation')}
            </AgentNewChatBtn>
            <Tooltip label={searchOpen ? t('workspace.closeSearch') : t('workspace.searchConversation')} position="bottom">
              <AgentSearchBtn
                type="button"
                $active={searchOpen}
                onClick={() => setSearchOpen((v) => !v)}
              >
                <Search size={14} />
              </AgentSearchBtn>
            </Tooltip>
          </AgentQuickActionWrap>
        )}

        {/* 팀 워크스페이스는 통합 에이전트 전용. 라이브 에이전트 모드에서는
            개인 세션만 지원하므로 팀 공유 대화 섹션을 렌더하지 않는다. */}
        {hasTeams && !isLiveAgentMode && (
          <>
            <SectionHeader
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('teamWorkspace')}
              onKeyDown={(e) => handleSectionHeaderKeyToggle(e, 'teamWorkspace')}
            >
              <SectionTitle>{t('sidebar.teamWorkspace')}</SectionTitle>
              <SectionRight>
                {sectionOpen.teamWorkspace ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </SectionRight>
            </SectionHeader>
            {sectionOpen.teamWorkspace && (
              <TeamWorkspaceWrap>
                <TeamSelectBtn
                  ref={teamDropdownBtnRef}
                  type="button"
                  onClick={() => {
                    const rect = teamDropdownBtnRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setTeamMenuWidth(rect.width);
                    setTeamMenu({
                      open: true,
                      x: rect.left,
                      y: rect.bottom + 4,
                    });
                  }}
                >
                  <span>
                    {teams.find((team) => team.team_id === selectedTeamId)?.name ?? t('sidebar.selectTeam')}
                  </span>
                  <ChevronDown size={14} />
                </TeamSelectBtn>
                <TeamSharedList>
                  {teamSharedEntries.length === 0 ? (
                    <TeamHint>{t('workspace.noSharedGroupChat')}</TeamHint>
                  ) : (
                    <>
                      {pinnedTeamSharedEntries.length > 0 && (
                        <>
                          <GroupTitle>{t('page.pinned')}</GroupTitle>
                          {pinnedTeamSharedEntries.map(renderTeamSharedEntry)}
                          {unpinnedTeamSharedEntries.length > 0 && <TeamSharedPinnedDivider />}
                        </>
                      )}
                      {unpinnedTeamSharedEntries.map(renderTeamSharedEntry)}
                    </>
                  )}
                </TeamSharedList>
              </TeamWorkspaceWrap>
            )}
          </>
        )}

        {(isAgentMode || isLiveAgentMode) && searchOpen && (
          <SearchInputWrap>
            <Search size={13} />
            <Input
              variant="ghost"
              ref={searchInputRef}
              type="text"
              placeholder={t('workspace.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSearchOpen(false);
              }}
            />
            {searchQuery && (
              <SearchClearBtn type="button" onClick={() => setSearchQuery('')}>
                <X size={11} />
              </SearchClearBtn>
            )}
          </SearchInputWrap>
        )}

        <SectionHeader
          role="button"
          tabIndex={0}
          onClick={() => toggleSection('recent')}
          onKeyDown={(e) => handleSectionHeaderKeyToggle(e, 'recent')}
        >
          <SectionTitle>{t('workspace.recentItems')}</SectionTitle>
          <SectionRight>
            {sectionOpen.recent ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </SectionRight>
        </SectionHeader>
        {sectionOpen.recent && (
          <RecentWrap>
            {filteredRecentGroups.length === 0 ? (
              <EmptyHint>
                {searchQuery.trim() ? t('search.noResults') : t('workspace.noRecentItems')}
              </EmptyHint>
            ) : listScrollParent ? (
              <RecentVirtuosoWrap>
                <Virtuoso
                  customScrollParent={listScrollParent}
                  data={recentVirtualRows}
                  overscan={280}
                  increaseViewportBy={{ top: 180, bottom: 360 }}
                  computeItemKey={(_, row) => row.key}
                  itemContent={(_, row) => {
                    if (row.type === 'group') {
                      return <GroupTitle>{row.label}</GroupTitle>;
                    }
                    const conv = recentConversationMap[row.id];
                    if (!conv) return null;
                    return (
                      <SidebarItem
                        key={row.id}
                        conversation={conv}
                        isActive={row.id === activeId}
                      />
                    );
                  }}
                />
              </RecentVirtuosoWrap>
            ) : (
              filteredRecentGroups.map((group, groupIndex) => (
                <React.Fragment key={`${group.label || 'slides'}:${groupIndex}`}>
                  {!searchQuery.trim() && !!group.label && <GroupTitle>{group.label}</GroupTitle>}
                  {group.ids.map((id) => {
                    const conv = recentConversationMap[id];
                    if (!conv) return null;
                    return (
                      <SidebarItem
                        key={id}
                        conversation={conv}
                        isActive={id === activeId}
                      />
                    );
                  })}
                </React.Fragment>
              ))
            )}
            {hasMoreItems && (
              <LoadMoreTrigger ref={loadMoreCallback}>
                {loadingMoreItems && <Loader size={14} className="spin" />}
              </LoadMoreTrigger>
            )}
          </RecentWrap>
        )}
      </List>
      <DropdownMenu
        open={teamMenu.open}
        x={teamMenu.x}
        y={teamMenu.y}
        width={teamMenuWidth}
        minWidth={teamMenuWidth}
        onClose={() => setTeamMenu((prev) => ({ ...prev, open: false }))}
        items={teams.map((team) => ({
          id: team.team_id,
          label: team.name,
          icon: selectedTeamId === team.team_id ? <Check size={14} /> : undefined,
          onClick: () => setSelectedTeamId(team.team_id),
        }))}
      />
      <DropdownMenu
        open={nodeCreateMenu.open}
        x={nodeCreateMenu.x}
        y={nodeCreateMenu.y}
        minWidth={160}
        onClose={() => setNodeCreateMenu((prev) => ({ ...prev, open: false }))}
        items={[
          {
            id: 'create-sub-folder',
            label: t('workspace.createSubFolder'),
            onClick: () => {
              if (!nodeCreateMenu.parentId) return;
              handleCreateWorkspace({
                scope: nodeCreateMenu.scope,
                teamId: nodeCreateMenu.teamId,
                parentId: nodeCreateMenu.parentId,
                kind: 'folder',
              });
            },
            disabled: !nodeCreateMenu.parentId,
          },
          {
            id: 'create-sub-page',
            label: getSubPageLabel(mode),
            onClick: () => {
              if (!nodeCreateMenu.parentId) return;
              handleCreateWorkspace({
                scope: nodeCreateMenu.scope,
                teamId: nodeCreateMenu.teamId,
                parentId: nodeCreateMenu.parentId,
                kind: 'page',
              });
            },
            disabled: !nodeCreateMenu.parentId,
          },
        ]}
      />
      <DropdownMenu
        open={nodeMenu.open}
        x={nodeMenu.x}
        y={nodeMenu.y}
        direction={nodeMenu.direction}
        align="right"
        minWidth={150}
        onClose={() => setNodeMenu((prev) => ({ ...prev, open: false }))}
        items={nodeMenuItems}
      />
    </Container>
  );
};
